import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  orderBy,
  query,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { auth } from "../firebaseConfig";

export default function PageItemsTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPageItems = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "pageItems"), orderBy("order", "asc"));
      const snapshot = await getDocs(q);

      const data = snapshot.docs.map((docItem) => ({
        id: docItem.id,
        ...docItem.data(),
        rolesDisplay: Array.isArray(docItem.data().roles)
          ? docItem.data().roles.join(", ")
          : "",
        tagsDisplay: Array.isArray(docItem.data().tags)
          ? docItem.data().tags.join(", ")
          : "",
      }));

      setRows(data);
    } catch (error) {
      console.error("Error loading page items:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPageItems();
  }, [loadPageItems]);

  const handleAddPageItem = async () => {
    try {
      const newItem = {
        key: "new-item",
        title: "New Item",
        description: "",
        url: "https://example.com",
        icon: "📄",
        pageId: "",
        folderId: null,
        visibility: "global",
        roles: [],
        tags: [],
        order: rows.length,
        enabled: true,
        target: "_blank",
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || "admin",
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || "admin",
      };

      await addDoc(collection(db, "pageItems"), newItem);
      await loadPageItems();
    } catch (error) {
      console.error("Error adding page item:", error);
    }
  };

  const handleDeletePageItem = async (id) => {
    if (!window.confirm("Are you sure you want to delete this page item?")) return;

    try {
      await deleteDoc(doc(db, "pageItems", id));
      await loadPageItems();
    } catch (error) {
      console.error("Error deleting page item:", error);
    }
  };

  const processRowUpdate = async (newRow, oldRow) => {
    try {
      const updatePayload = {
        key: newRow.key,
        title: newRow.title,
        description: newRow.description || "",
        url: newRow.url,
        icon: newRow.icon || "",
        pageId: newRow.pageId,
        folderId: newRow.folderId || null,
        visibility: newRow.visibility || "global",
        roles: newRow.rolesDisplay
          ? newRow.rolesDisplay.split(",").map((r) => r.trim()).filter(Boolean)
          : [],
        tags: newRow.tagsDisplay
          ? newRow.tagsDisplay.split(",").map((t) => t.trim()).filter(Boolean)
          : [],
        order: Number(newRow.order) || 0,
        enabled: Boolean(newRow.enabled),
        target: newRow.target || "_blank",
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || "unknown",
      };

      const docRef = doc(db, "pageItems", newRow.id);
      await updateDoc(docRef, updatePayload);

      const updatedRow = {
        ...oldRow,
        ...newRow,
        roles: updatePayload.roles,
        tags: updatePayload.tags,
      };

      setRows((prev) =>
        prev.map((row) => (row.id === newRow.id ? updatedRow : row))
      );

      return updatedRow;
    } catch (error) {
      console.error("Error updating page item:", error);
      throw error;
    }
  };

  const columns = [
    { field: "key", headerName: "Key", width: 150, editable: true },
    { field: "title", headerName: "Title", width: 180, editable: true },
    { field: "description", headerName: "Description", width: 200, editable: true },
    { field: "url", headerName: "URL", width: 280, editable: true },
    { field: "icon", headerName: "Icon", width: 80, editable: true },
    { field: "pageId", headerName: "Page ID", width: 120, editable: true },
    { field: "folderId", headerName: "Folder ID", width: 140, editable: true },
    {
      field: "rolesDisplay",
      headerName: "Roles (comma-separated)",
      width: 220,
      editable: true,
    },
    {
      field: "tagsDisplay",
      headerName: "Tags (comma-separated)",
      width: 200,
      editable: true,
    },
    {
      field: "order",
      headerName: "Order",
      width: 100,
      type: "number",
      editable: true,
    },
    {
      field: "enabled",
      headerName: "Enabled",
      width: 100,
      type: "boolean",
      editable: true,
    },
    {
      field: "target",
      headerName: "Target",
      width: 100,
      editable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={() => handleDeletePageItem(params.row.id)}
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", height: 700 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Portal Page Items</Typography>
        <Button variant="contained" size="small" onClick={handleAddPageItem}>
          Add Page Item
        </Button>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        editMode="row"
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          console.error("Failed to save row update:", error);
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
        }}
      />
    </Box>
  );
}
